import React, { useEffect, useRef, useState } from "react";

// region External libraries
import _ from "lodash";
import { Add } from "@mui/icons-material";
import { SetStateAction, useAtom, WritableAtom } from "@libraries/jotai";
import Yup from "@libraries/yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { uuidv4 } from "src/libraries/uuid";
// endregion External libraries
// region Helpers
import { getAllClaimsAttachedToUser } from "src/helpers/utils";
import { EPermissionGroupModule, permissionGroupObject } from "src/helpers/permissionGroup";
// endregion Helpers
// region Hooks
import { useIdentity, PERMISSIONS } from "@hooks/useIdentity";
// endregion Hooks
// region Languages
import { GlobalMessages, UserRegisterMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Atoms
import TabPanel from "@atoms/TabPanel";
import Loading from "@atoms/Loading";
import PageTitle from "@atoms/PageTitle";
import ActionDialog from "@atoms/ActionDialog";
import { selectedModuleIdAtom } from "@atoms/SelectIdentityModule";
import AccessDenied from "@atoms/AccessDenied";
// endregion Atoms
// region Molecules
import FormUser from "@molecules/FormUser";
import GroupSelectionList from "@molecules/GroupSelectionList";
// region Molecules
// region Organisms
import ModuleAccessSection, { ModuleAccessSectionHandle } from "@organisms/ModuleAccessSection";
import { useTabsAtom } from "@organisms/PageTabs";
import StepTabs, { IStepTabProps, validateStepTabForm } from "@organisms/StepTabs";
import PermissionGroup, { EPermissionGroupOrigin } from "src/organisms/PermissionGroup";
// endregion Organisms
// region Pages
import { userRightsTabsAtom, selectedTabAtom } from "@pages/UsersRights";
// endregion Pages
// region Store
import { TUserByIdQueries, userById } from "src/store/graphql/groups/user/query.graphql";
import {
  ECommandResultStatus,
  EWhereClausesOperators,
  PermissionGroupQueriesGetAllWithPaginationArgs,
  PermissionGroupType,
  RoleClaimQueriesGetAllWithPaginationArgs,
  RoleClaimType,
  UserClaimMutationsSaveUserClaimsArgs,
  UserClaimType,
  UserMutationsCreateArgs,
  UserQueriesGetByIdArgs,
  UserType
} from "src/store/graphql/helpers/types.graphql";
import {
  TUserCreateMutations,
  userCreate
} from "src/store/graphql/groups/user/mutation.graphql";
import { TPermissionGroupAllWithPaginationQueries, permissionGroupAllWithPagination } from "src/store/graphql/groups/permissionGroup/query.graphql";
import { roleClaimAllWithPagination, TRoleClaimAllWithPaginationQueries } from "src/store/graphql/groups/roleClaim/query.graphql";
import { saveUserClaims, TSaveUserClaimsMutations } from "src/store/graphql/groups/userClaim/mutation.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IUserProps {
  selectedStepTabAtom: WritableAtom<number, SetStateAction<number>, void>;
  mode: "CREATE" | "EDIT";
  currentUserId?: string | null;
}

export interface IFormUserInputs {
  email: string;
  name: string;
  imageUrl?: string;
  position: string;
  description?: string;
  active: boolean;
  roleId?: string;
  controlRoleId?: string;
  userClaims?: any[]; // UserClaimType[]
}

enum EStepTabPanelIndexes {
  formUser = 0,
  addUserToGroups = 1,
  moduleAccess = 2,
  dynamicStepTab = 3
}

const User: React.FC<IUserProps> = ({ selectedStepTabAtom, mode, currentUserId }) => {
  // region Queries
  const [QueryGetUserById] = useLazyQuery<TUserByIdQueries, UserQueriesGetByIdArgs>(
    userById([
      "id", "name", "email", "position", "imageUrl", "description", "active", "userRoles.role.id",
      "userRoles.role.name", "userRoles.role.moduleId", "userRoles.role.roleClaims.claimId",
      "userClaims.claimId", "userClaims.revoked"
    ])
  );
  const [QueryGetPermissionGroupAllWithPagination] = useLazyQuery<TPermissionGroupAllWithPaginationQueries,
    PermissionGroupQueriesGetAllWithPaginationArgs>(
      permissionGroupAllWithPagination([
        "id", "alias"
      ])
    );
  const [QueryGetRoleClaimAllWithPagination] = useLazyQuery<TRoleClaimAllWithPaginationQueries,
    RoleClaimQueriesGetAllWithPaginationArgs>(
      roleClaimAllWithPagination([
        "claimId"
      ])
    );
  // endregion Queries
  // region Mutations
  const [MutationCreateUser] = useMutation<TUserCreateMutations, UserMutationsCreateArgs>(
    userCreate()
  );
  const [MutationUpdateUser] = useMutation<TSaveUserClaimsMutations,
    UserClaimMutationsSaveUserClaimsArgs>(saveUserClaims());
  // endregion Mutations
  // region Hook
  const {
    pageTabs,
    setPageTabs,
    selectedTab,
    setSelectedTab
  } = useTabsAtom(userRightsTabsAtom, selectedTabAtom);
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [selectedStepTab, setSelectedStepTab] = useAtom(selectedStepTabAtom);
  const [selectedModuleId] = useAtom(selectedModuleIdAtom);
  const { validateUserAccess } = useIdentity();
  const moduleAccessRef = useRef<ModuleAccessSectionHandle>(null);
  // endregion Hooks

  // region Schemas
  const userFormSchema = Yup.object().shape({
    email: Yup.string().required().email().max(100),
    name: Yup.string().required().max(100),
    position: Yup.string().required().max(50),
    description: Yup.string().optional().max(255)
  });
  // endregion Schemas

  // region Form
  const methods = useForm<IFormUserInputs>({
    resolver: yupResolver(userFormSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });
  const { setValue, getValues, watch } = methods;
  // endregion Form

  // region Constants
  const roleIdWatch = watch("roleId");

  const steps: IStepTabProps[] = [
    {
      id: "stepsUserRegister_userTab",
      tabLabel: UserRegisterMessages.stepTabFormUser,
      schema: userFormSchema
    },
    {
      id: "stepsUserRegister_addUserToRoleTab",
      tabLabel: UserRegisterMessages.stepTabAddUserToRoles
    },
    // Module Access tab is only meaningful when editing an existing user —
    // a new user has no id yet to attach UserModuleAccess rows to.
    ...(mode === "EDIT" && currentUserId ? [{
      id: "stepsUserRegister_moduleAccessTab",
      tabLabel: UserRegisterMessages.stepTabModuleAccess
    }] : [])
  ];
  // endregion Constants

  // region States
  const [isLoading, setIsLoading] = useState(false);
  const [currentSteps, setCurrentSteps] = useState<IStepTabProps[]>(steps);
  const [selectedClaims, setSelectedClaims] = useState<{claimId: string}[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroupType[]>([]);
  const [selectedClaimsOfCurrentRole, setSelectedClaimsOfCurrentRole] = useState<RoleClaimType[]>([]);
  const [openConfirmUserEditModal, setOpenConfirmUserEditModal] = useState(false);
  const [openInactivateUserConfirmationModal, setOpenInactivateUserConfirmationModal] = useState(false);
  const [initialStatusActive, setInitialStatusActive] = useState(false);
  const [pageTitle, setPageTitle] = useState<string>(
    mode === "CREATE" ? t(UserRegisterMessages.titleCreate) : t(UserRegisterMessages.titleEdit)
  );
  // endregion States

  // region Validations
  const validateFormUsers = async (indexNewTab: number) => validateStepTabForm(currentSteps, methods, indexNewTab);

  const validateSelectedRole = async (indexNewTab: number) => {
    if (!roleIdWatch) {
      disablePermissionGroupSteps(true);

      return {
        stepTabIsAvailable: false,
        indexNextAvailableStepTab: selectedStepTab,
        toastMessage: t(UserRegisterMessages.roleNotSelected),
        additionalToastMessages: [],
        toastMessageType: "info"
      };
    }

    if (getValues("controlRoleId") !== roleIdWatch) {
      const roleClaims = await getRoleClaimsOfCurrentRole(roleIdWatch);

      const userSelectedClaims = getAllClaimsAttachedToUser(getValues("userClaims") as UserClaimType[], roleClaims);

      setSelectedClaims(userSelectedClaims);
      setSelectedClaimsOfCurrentRole(roleClaims);
    }

    disablePermissionGroupSteps(false);

    return {
      stepTabIsAvailable: true,
      indexNextAvailableStepTab: indexNewTab,
      toastMessage: "",
      additionalToastMessages: [],
      toastMessageType: "success"
    };
  };

  const onChangeTab = async (indexNewTab: number) => {
    const validations = [
      {
        index: EStepTabPanelIndexes.formUser,
        callback: () => validateFormUsers(indexNewTab),
        stepTabIsInvisible: false
      },
      {
        index: EStepTabPanelIndexes.addUserToGroups,
        callback: () => validateSelectedRole(indexNewTab),
        stepTabIsInvisible: false
      }
    ];

    setIsLoading(true);

    const promises = validations.filter(
      ({ index, stepTabIsInvisible }) => index >= selectedStepTab && index < indexNewTab && !stepTabIsInvisible
    ).map(({ callback }) => callback);

    const results: any[] = [];

    for (const promise of promises) {
      // eslint-disable-next-line no-await-in-loop
      const response = await promise();

      results.push(response);
    }

    const indexNextStepTabWithError = results.findIndex(({ stepTabIsAvailable }) => !stepTabIsAvailable);
    const thereIsAStepTabWithError = indexNextStepTabWithError >= 0;

    setIsLoading(false);

    if (!thereIsAStepTabWithError) return validateStepTabForm(currentSteps, methods, indexNewTab);

    const promiseOfReturn = async () => ({
      stepTabIsAvailable: results[indexNextStepTabWithError].stepTabIsAvailable,
      indexNextAvailableStepTab: results[indexNextStepTabWithError].indexNextAvailableStepTab,
      toastMessage: results[indexNextStepTabWithError].toastMessage,
      additionalToastMessages: results[indexNextStepTabWithError].additionalToastMessages,
      toastMessageType: results[indexNextStepTabWithError].toastMessageType
    });

    return promiseOfReturn();
  };
  // endregion Validations

  // region Functions
  const updateSelectedClaims = (claimIds: string[]) => {
    const updatedUserClaims = claimIds.map((claimId) => ({ claimId } as UserClaimType));

    setSelectedClaims(updatedUserClaims);
  };

  const updateRoleId = (roleId: string) => {
    const roleIdToSet = roleIdWatch === roleId ? undefined : roleId;

    setValue("roleId", roleIdToSet);
  };

  const disablePermissionGroupSteps = (disabled: boolean) => {
    const cloneCurrentStepTabs = _.cloneDeep(currentSteps);

    const disabledCloneSteps = cloneCurrentStepTabs
      .map((item, index) => {
        if (index > selectedStepTab) {
          return { ...item, disabled };
        }

        return item;
      });

    setCurrentSteps(disabledCloneSteps);
  };

  const getUserById = async (userId: string) => {
    try {
      const query = await QueryGetUserById({ variables: { id: userId } });

      const response = query.data?.user.getByID;

      if (response?.status !== ECommandResultStatus.SUCCESS) return {} as UserType;

      return response.result as UserType;
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return {} as UserType;
    }
  };

  const getPermissionGroups = async () => {
    try {
      const query = await QueryGetPermissionGroupAllWithPagination({
        variables: {
          where: [
            {
              property: "moduleId",
              operator: EWhereClausesOperators.EQUALS,
              value: selectedModuleId
            }
          ]
        }
      });

      const response = query.data?.permissionGroup.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as PermissionGroupType[];

      return response.result as PermissionGroupType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as PermissionGroupType[];
    }
  };

  const getRoleClaimsOfCurrentRole = async (currentRoleId: string) => {
    try {
      const query = await QueryGetRoleClaimAllWithPagination(
        {
          variables: {
            where: [
              {
                property: "RoleId",
                operator: EWhereClausesOperators.EQUALS,
                value: currentRoleId
              }
            ]
          }
        }
      );

      const response = query.data?.roleClaim.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as RoleClaimType[];

      return response.result as RoleClaimType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as RoleClaimType[];
    }
  };

  const handleEndOfProcess = async () => {
    // The Module Access tab manages its own GraphQL mutations through an
    // imperative `save()` handle. We route the Save click here so the user
    // can use the same StepTabs Save button as the other tabs without the
    // generic user-claim flow firing for an unrelated tab.
    if (selectedStepTab === EStepTabPanelIndexes.moduleAccess && moduleAccessRef.current) {
      try {
        setIsLoading(true);
        await moduleAccessRef.current.save();
      } finally {
        setIsLoading(false);
      }

      return;
    }

    const formValidation = await validateStepTabForm(currentSteps, methods, selectedStepTab + 1);
    const roleId = getValues("roleId");

    if (!formValidation.stepTabIsAvailable) return;

    if (initialStatusActive && !getValues("active") && !openInactivateUserConfirmationModal) {
      setOpenInactivateUserConfirmationModal(true);

      return;
    }

    if (mode === "EDIT" && !roleId && !openConfirmUserEditModal) {
      setOpenConfirmUserEditModal(true);

      return;
    }

    const createUser = async () => {
      const mutation = await MutationCreateUser({
        variables: {
          userCreateInput: {
            name: getValues("name"),
            email: getValues("email"),
            imageUrl: getValues("imageUrl") || undefined,
            position: getValues("position"),
            description: getValues("description"),
            roleId: getValues("roleId")
          }
        }
      });

      return mutation.data?.user.create;
    };

    const updateUser = async () => {
      const claimsToSet = roleId ? selectedClaims.map((claim) => claim.claimId) : [];
      const revokedClaimsToSet = roleId ? _.differenceBy(selectedClaimsOfCurrentRole, selectedClaims, "claimId").map(
        (claim) => claim.claimId
      ) : undefined;

      const mutation = await MutationUpdateUser({
        variables: {
          saveUserClaimsInput: {
            userUpdateInput: {
              id: currentUserId!,
              name: getValues("name"),
              email: getValues("email"),
              imageUrl: getValues("imageUrl") || undefined,
              position: getValues("position"),
              description: getValues("description"),
              active: getValues("active")
            },
            roleId,
            moduleId: selectedModuleId,
            claims: claimsToSet,
            revokedClaims: revokedClaimsToSet
          }
        }
      });

      return mutation.data?.userClaim.saveUserClaims;
    };

    try {
      setIsLoading(true);

      const response = mode === "CREATE" ? await createUser() : await updateUser();

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      addToast({ type: "success", title: response.message });

    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });
    } finally {
      setIsLoading(false);
    }

    const updatedTabs = pageTabs.filter((_value, index) => index !== selectedTab);

    setSelectedTab(0);
    setPageTabs(updatedTabs);
  };

  const onDeleteUserForm = () => {
    const updatedTabs = pageTabs.filter((_value, index) => index !== selectedTab);

    setSelectedTab(0);
    setPageTabs(updatedTabs);
  };
  // endregion Functions

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const permissionGroups = await getPermissionGroups();

      if (currentUserId) {
        const currentUser = await getUserById(currentUserId);

        const userRoleOfSelectedModule = currentUser.userRoles
          ?.find((userRole) => userRole?.role?.moduleId === selectedModuleId);
        let roleTitle = "";

        if (userRoleOfSelectedModule) {
          roleTitle = ` - ${userRoleOfSelectedModule.role?.name}`;
        } else {
          disablePermissionGroupSteps(true);
        }

        setValue("email", currentUser.email);
        setValue("name", currentUser.name);
        setValue("imageUrl", currentUser.imageUrl ?? undefined);
        setValue("position", currentUser.position);
        setValue("description", currentUser.description ?? undefined);
        setValue("active", currentUser.active);
        setValue("roleId", userRoleOfSelectedModule?.role?.id);
        setValue("controlRoleId", userRoleOfSelectedModule?.role?.id);
        setValue("userClaims", currentUser.userClaims ?? []);

        const userSelectedClaims = getAllClaimsAttachedToUser(
          getValues("userClaims") as UserClaimType[],
          userRoleOfSelectedModule?.role?.roleClaims as RoleClaimType[]
        );

        setPermissionGroups(permissionGroups);
        setSelectedClaims(userSelectedClaims);
        setSelectedClaimsOfCurrentRole(userRoleOfSelectedModule?.role?.roleClaims as RoleClaimType[]);
        setPageTitle(`${t(UserRegisterMessages.titleEdit)} - ${currentUser.name}${roleTitle}`);
        setInitialStatusActive(currentUser.active);
      }
      setIsLoading(false);

      if (mode === "CREATE") return;

      const permissionSteps = permissionGroups.map((permissionGroup) => ({
        id: permissionGroup.id,
        tabLabel: permissionGroupObject[permissionGroup.alias as EPermissionGroupModule].title
      } as IStepTabProps));

      setCurrentSteps([...currentSteps, ...permissionSteps]);
    })();

    return () => {
      setPermissionGroups([]);
    };
  }, []);

  const hasPermission = mode === "CREATE"
    ? validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.USERS.INCLUDE)
    : validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.USERS.ALTER);

  if (!hasPermission) {
    return <AccessDenied />;
  }

  return (
    <Styled.Container>
      <Loading loading={isLoading} />
      <PageTitle>{pageTitle}</PageTitle>
      <StepTabs
        content={currentSteps}
        selectedStepTab={selectedStepTab}
        setSelectedStepTab={setSelectedStepTab}
        mode={mode}
        // empty function because there is no finish process button on the final step
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onFinish={() => {}}
        onChangeTab={onChangeTab}
        showConfirmButtonOnLastStepTab={false}
        additionalControlButtons={
         [{
           id: "saveButton",
           displayFromStepTab: 1,
           extended: true,
           onClick: () => handleEndOfProcess(),
           content: (
             <Styled.SaveUserButton>
               {t(GlobalMessages.saveText)}
               <Add fontSize="large" />
             </Styled.SaveUserButton>
           )
         }]
        }
      >
        <TabPanel
          id="full-width-formUserStepTabPanel"
          value={selectedStepTab}
          index={EStepTabPanelIndexes.formUser}
          unmountOnExit
        >
          <FormUser controlForms={methods} mode={mode} currentUserId={currentUserId} onDelete={onDeleteUserForm} />
        </TabPanel>
        <TabPanel
          id="full-width-addUserToGroupsStepTabPanel"
          value={selectedStepTab}
          index={EStepTabPanelIndexes.addUserToGroups}
          unmountOnExit
        >
          <GroupSelectionList
            onRoleChange={updateRoleId}
            currentRoleId={roleIdWatch}
            isSelectedStep={selectedStepTab === EStepTabPanelIndexes.addUserToGroups}
            onSelectedStepChange={() => { setValue("controlRoleId", roleIdWatch); }}
          />
        </TabPanel>
        {mode === "EDIT" && currentUserId && (
          <TabPanel
            id="full-width-moduleAccessStepTabPanel"
            value={selectedStepTab}
            index={EStepTabPanelIndexes.moduleAccess}
            unmountOnExit
          >
            <ModuleAccessSection ref={moduleAccessRef} userId={currentUserId} />
          </TabPanel>
        )}
        {mode === "EDIT" && permissionGroups.map((permissionGroup, index) => (
          <TabPanel
            key={permissionGroup.id}
            id={`full-width-${permissionGroup.name}StepTabPanel`}
            value={selectedStepTab}
            index={EStepTabPanelIndexes.dynamicStepTab + index}
            unmountOnExit
          >
            <PermissionGroup
              permissionGroupId={permissionGroup.id}
              origin={EPermissionGroupOrigin.USER}
              onClaimGroupsChange={updateSelectedClaims}
              selectedClaims={selectedClaims}
              selectedClaimsOfCurrentRole={selectedClaimsOfCurrentRole}
            />
          </TabPanel>
        ))}
      </StepTabs>
      <ActionDialog
        open={openConfirmUserEditModal}
        onClose={() => setOpenConfirmUserEditModal(false)}
        title={t(UserRegisterMessages.confirmUserEditModalTitle)}
        actions={[
          { text: t(GlobalMessages.buttonCancel), action: () => { setOpenConfirmUserEditModal(false); } },
          { text: t(GlobalMessages.buttonConfirm), action: () => { handleEndOfProcess(); } }
        ]}
      >
        {t(UserRegisterMessages.confirmUserEditModalDescription)}
      </ActionDialog>
      <ActionDialog
        open={openInactivateUserConfirmationModal}
        onClose={() => setOpenInactivateUserConfirmationModal(false)}
        title={t(UserRegisterMessages.confirmUserInactivateModalTitle)}
        actions={[
          { text: t(GlobalMessages.noText), action: () => { setOpenInactivateUserConfirmationModal(false); } },
          { text: t(GlobalMessages.yesText), action: () => { handleEndOfProcess(); } }
        ]}
      >
        {t(UserRegisterMessages.confirmUserInactivateModalDescription)}
      </ActionDialog>
    </Styled.Container>
  );
};

User.defaultProps = {
  currentUserId: null
};

export default User;